<template>
  <b-sidebar
    :visible="visible"
    right
    shadow
    backdrop
    width="470px"
    @change="$emit('change', $event)"
    @shown="ready = true"
    @hidden="ready = false"
  >
    <template #title>
      <div class="drawer-title">
        <div class="drawer-title-name">สาขา{{ branchName }}</div>
        <b-link class="drawer-title-link" @click="goFull">เปิด Branch Dashboard เต็ม ▸</b-link>
      </div>
    </template>

    <!-- Loaded state -->
    <div class="px-3 py-2 drawer-body" v-if="d">

      <!-- รายได้ในช่วง + ฿/ใบเสร็จ -->
      <b-row class="mb-2">
        <b-col cols="6">
          <b-card class="h-100 drawer-stat-card" body-class="p-3">
            <div class="label-muted small">รายได้ในช่วงนี้</div>
            <div class="d-flex align-items-baseline mt-1" style="gap: 6px">
              <span class="drawer-num font-weight-bold">{{ formatShortBaht(d.hero.revenue.value) }}</span>
              <span class="small text-muted">฿</span>
            </div>
            <trend-badge class="mt-1" :pct="d.hero.revenue.prevPeriodPct" label="vs ช่วงก่อน" />
          </b-card>
        </b-col>
        <b-col cols="6">
          <b-card class="h-100 drawer-stat-card" body-class="p-3">
            <div class="label-muted small">฿ ต่อใบเสร็จ</div>
            <div class="d-flex align-items-baseline mt-1" style="gap: 6px">
              <span class="drawer-num font-weight-bold">{{ formatNumber(d.hero.perCase.value) }}</span>
              <span class="small text-muted">฿</span>
            </div>
            <trend-badge class="mt-1" :pct="d.hero.perCase.prevPeriodPct" label="vs ช่วงก่อน" />
          </b-card>
        </b-col>
      </b-row>

      <!-- Revenue target (pro-rated to the viewed range) -->
      <div v-if="d.revenueTarget" class="mb-3">
        <target-progress
          variant="full"
          :revenue-target="d.revenueTarget"
          :actual="d.hero.revenue.value"
          :start-dt="startDt"
          :end-dt="endDt"
        />
      </div>

      <!-- Mini stats 4 ช่อง -->
      <b-row class="mb-3">
        <b-col cols="3" class="px-1">
          <b-card class="text-center drawer-mini-card" body-class="py-2 px-1">
            <div class="drawer-mini-num font-weight-bold">{{ d.stats.patients.total }}</div>
            <div class="mini-stat-label">ผู้ป่วย</div>
          </b-card>
        </b-col>
        <b-col cols="3" class="px-1">
          <b-card class="text-center drawer-mini-card" body-class="py-2 px-1">
            <div class="drawer-mini-num font-weight-bold text-green">+{{ d.stats.patients.new }}</div>
            <div class="mini-stat-label">ผู้ป่วยใหม่</div>
          </b-card>
        </b-col>
        <b-col cols="3" class="px-1">
          <b-card class="text-center drawer-mini-card" body-class="py-2 px-1">
            <div
              class="drawer-mini-num font-weight-bold"
              :class="d.stats.noShow.pct >= 5 ? 'text-red' : ''"
            >
              {{ d.stats.noShow.pct != null ? d.stats.noShow.pct.toFixed(1) + '%' : d.stats.noShow.count }}
            </div>
            <div class="mini-stat-label">No-show</div>
            <div v-if="d.stats.noShow.total != null" class="mini-stat-sub">
              {{ d.stats.noShow.count }}/{{ d.stats.noShow.total }} นัด
            </div>
          </b-card>
        </b-col>
        <b-col cols="3" class="px-1">
          <b-card class="text-center drawer-mini-card" body-class="py-2 px-1">
            <div class="drawer-mini-num font-weight-bold">{{ d.stats.receiptsIssued }}</div>
            <div class="mini-stat-label">ใบเสร็จ</div>
          </b-card>
        </b-col>
      </b-row>

      <!-- BarChart ยูนิตรายชั่วโมง -->
      <div v-if="d.hourlyUnits && d.hourlyUnits.unitCount" class="mb-3">
        <div class="font-weight-bold small mb-1">
          การใช้ยูนิตรายชั่วโมง · เฉลี่ยในช่วง
          <span class="label-muted font-weight-normal">
            · ทั้งหมด {{ d.hourlyUnits.unitCount }} ยูนิต
          </span>
        </div>
        <!-- :styles sets the height on vue-chartjs's own wrapper div; without it
             the canvas has no fixed parent height and balloons past the box,
             clipping the bars + x-axis (maintainAspectRatio:false). -->
        <div v-if="ready">
          <bar-chart
            :chart-data="unitChartData"
            :options="unitChartOptions"
            :styles="{ position: 'relative', height: '130px' }"
          />
        </div>
      </div>

      <!-- รายได้หมอ -->
      <div v-if="d.doctors && d.doctors.length" class="mb-3">
        <div class="font-weight-bold small mb-1">รายได้ทันตแพทย์ · ในช่วง</div>
        <div class="d-flex flex-column" style="gap: 8px">
          <div v-for="(doc, i) in sortedDoctors" :key="i">
            <div class="d-flex justify-content-between align-items-baseline mb-1">
              <span class="small">{{ doc.name }}</span>
              <span class="small font-weight-bold">{{ formatNumber(doc.revenue) }} ฿</span>
            </div>
            <div class="doctor-bar-track">
              <div
                class="doctor-bar-fill"
                :style="{ width: doctorBarWidth(doc.revenue) + '%' }"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Case mix PieChart -->
      <div v-if="caseMixItems && caseMixItems.length" class="mb-3">
        <div class="font-weight-bold small mb-1">ประเภทการรักษา · ในช่วง</div>
        <div class="d-flex align-items-center" style="gap: 12px">
          <div v-if="ready" style="flex-shrink: 0">
            <pie-chart
              :chart-data="caseMixChartData"
              :options="caseMixOptions"
              :styles="{ position: 'relative', width: '116px', height: '116px' }"
            />
          </div>
          <div class="flex-fill" style="min-width: 0">
            <div
              v-for="(item, i) in caseMixItems"
              :key="i"
              class="d-flex justify-content-between align-items-center py-1"
            >
              <span class="d-flex align-items-center small" style="gap: 6px">
                <span class="legend-dot" :style="{ background: item.color }"></span>
                <span>{{ item.category }}</span>
              </span>
              <span class="small font-weight-bold">{{ item.pct }}%</span>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Loading / error state -->
    <div v-else class="text-center p-5">
      <b-spinner small />
    </div>
  </b-sidebar>
</template>

<script>
import { formatNumber } from "@/utils/utils";
import BarChart from "@/components/chart/BarChart.vue";
import PieChart from "@/components/chart/PieChart.vue";
import TrendBadge from "@/components/dashboard/TrendBadge.vue";
import TargetProgress from "@/components/dashboard/TargetProgress.vue";

const CASE_MIX_COLORS = [
  "#2196F3", "#26C6DA", "#8B5CF6", "#F59E0B", "#EF4444", "#94A3B8",
  "#22C55E", "#F97316", "#EC4899", "#64748B",
];

export default {
  name: "BranchDrawer",

  components: { BarChart, PieChart, TrendBadge, TargetProgress },

  props: {
    visible: { type: Boolean, default: false },
    branchUrl: { type: String, default: null },
    branchName: { type: String, default: "" },
    startDt: { type: String, default: null },
    endDt: { type: String, default: null },
  },

  data() {
    return {
      d: null,
      ready: false, // charts mount only once the drawer is fully shown, so Chart.js
                    // measures a settled container (avoids the slide-in mis-size).
    };
  },

  computed: {
    sortedDoctors() {
      if (!this.d || !this.d.doctors) return [];
      return [...this.d.doctors].sort((a, b) => (b.revenue || 0) - (a.revenue || 0));
    },
    maxDoctorRevenue() {
      if (!this.sortedDoctors.length) return 1;
      return this.sortedDoctors[0].revenue || 1;
    },

    unitChartData() {
      const h = this.d && this.d.hourlyUnits;
      if (!h || !h.hours) return { labels: [], datasets: [] };
      return {
        labels: h.hours.map((x) => String(x.hour).padStart(2, "0")),
        datasets: [
          {
            label: "การใช้งาน",
            data: h.hours.map((x) => x.avgOccupancyPct),
            backgroundColor: h.hours.map((x) => {
              const r = (x.avgOccupancyPct || 0) / 100;
              return r >= 0.75 ? "#22C55E" : r >= 0.5 ? "#2196F3" : "#F59E0B";
            }),
            borderRadius: 4,
          },
        ],
      };
    },
    unitChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          callbacks: {
            label: (tooltipItem) => "ใช้งาน " + tooltipItem.yLabel + "%",
          },
        },
        scales: {
          xAxes: [
            {
              gridLines: { display: false },
              ticks: { fontColor: "#6B7280", fontSize: 9 },
            },
          ],
          yAxes: [
            {
              ticks: {
                min: 0,
                max: 100,
                stepSize: 25,
                fontColor: "#6B7280",
                fontSize: 9,
                callback: (v) => v + "%",
              },
              gridLines: { color: "#E5EAF0" },
            },
          ],
        },
      };
    },

    caseMixItems() {
      if (!this.d || !this.d.caseMix || !this.d.caseMix.length) return [];
      return this.d.caseMix.map((item, i) => ({
        ...item,
        color: item.color || CASE_MIX_COLORS[i % CASE_MIX_COLORS.length],
      }));
    },
    caseMixChartData() {
      const items = this.caseMixItems;
      return {
        labels: items.map((c) => c.category),
        datasets: [
          {
            data: items.map((c) => c.pct),
            backgroundColor: items.map((c) => c.color),
            borderWidth: 2,
            borderColor: "#fff",
          },
        ],
      };
    },
    caseMixOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 58,
        legend: { display: false },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              const label = data.labels[tooltipItem.index] || "";
              const val = data.datasets[0].data[tooltipItem.index];
              return label + ": " + val + "%";
            },
          },
        },
      };
    },
  },

  watch: {
    branchUrl: { immediate: true, handler() { this.loadBranch(); } },
    startDt() { this.loadBranch(); },
    endDt() { this.loadBranch(); },
  },

  methods: {
    async loadBranch() {
      const v = this.branchUrl;
      if (!v) return;
      this.d = null;
      const reqUrl = v;
      const reqStart = this.startDt;
      const reqEnd = this.endDt;
      try {
        const res = await this.$store.dispatch("dashboard/fetchBranchDashboard", {
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: v,
          startDt: this.startDt,
          endDt: this.endDt,
        });
        // a newer branch or date range may have superseded this request while it was in flight
        if (reqUrl !== this.branchUrl || reqStart !== this.startDt || reqEnd !== this.endDt) return;
        this.d = res;
      } catch (e) {
        if (reqUrl === this.branchUrl && reqStart === this.startDt && reqEnd === this.endDt) this.d = null;
      }
    },
    goFull() {
      this.$emit("open-full", this.branchUrl);
    },
    formatNumber(val) {
      return formatNumber(val || 0);
    },
    formatShortBaht(val) {
      if (val == null) return "–";
      if (val >= 1000000) return (val / 1000000).toFixed(2) + "M";
      if (val >= 1000) return (val / 1000).toFixed(1) + "K";
      return formatNumber(val);
    },
    doctorBarWidth(revenue) {
      if (!this.maxDoctorRevenue) return 0;
      return Math.round((revenue / this.maxDoctorRevenue) * 100);
    },
  },
};
</script>

<style scoped>
.drawer-body {
  --bd-green: #22c55e;
  --bd-red: #ef4444;
  --bd-primary: #2196f3;
  --bd-primary-dark: #1976d2;
  --bd-border: #e5eaf0;
  --bd-bg: #f5f8fb;
  --bd-text-muted: #6b7280;
}

/* ---- Drawer title (b-sidebar header) ----
   Header sits outside .drawer-body, so size/color are set explicitly here
   to stay cohesive with the body instead of inheriting bootstrap-vue's 1.5rem. */
.drawer-title { line-height: 1.25; }
.drawer-title-name {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2a37;
}
.drawer-title-link {
  font-size: 12px;
  font-weight: 400;
  color: #1976d2;
}

.text-green      { color: var(--bd-green)  !important; }
.text-red        { color: var(--bd-red)    !important; }
.text-primary-dark { color: var(--bd-primary-dark) !important; }
.label-muted     { color: var(--bd-text-muted); }

.drawer-stat-card {
  border: 1px solid var(--bd-border);
  border-radius: 10px;
}
.drawer-num {
  font-size: 1.15rem;
  line-height: 1;
}

.drawer-mini-card {
  border: 1px solid var(--bd-border);
  border-radius: 8px;
}
.drawer-mini-num {
  font-size: 1rem;
  line-height: 1.2;
}
.mini-stat-label {
  font-size: 10.5px;
  color: var(--bd-text-muted);
  line-height: 1.35;
  margin-top: 2px;
}
.mini-stat-sub {
  font-size: 9.5px;
  color: var(--bd-text-muted);
  line-height: 1.3;
  opacity: 0.8;
}

.legend-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
}

.doctor-bar-track {
  position: relative;
  border-radius: 999px;
  background: var(--bd-bg);
  height: 6px;
}
.doctor-bar-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 999px;
  background: var(--bd-primary);
  transition: width 0.4s ease;
}
</style>
